/**
 * ship/interior.ts — Procedural cabin interior with window apertures
 * M2.5: Side walls rebuilt as four framed segments around 2.5m×1.2m window apertures
 *
 * Cabin: ~4m wide × 5m long × 2.6m high
 * Side windows: 2.5m wide × 1.2m high, sill at 0.9m, centered amidships
 * Canopy: 3 angled front panels
 * Budget: ≤ 250k tris, ≤ 30 draw calls
 */

import * as THREE from 'three';
import { createGlassMaterial } from './glass.js';

export function createCabin(): { cabin: THREE.Group; glassMeshes: THREE.Mesh[] } {
  const cabin = new THREE.Group();

  const W = 4.0;
  const L = 5.0;
  const H = 2.6;
  const halfW = W / 2;
  const halfL = L / 2;

  // Window aperture
  const WIN_W = 2.5;
  const WIN_H = 1.2;
  const WIN_SILL = 0.9;
  const FRAME_WIDTH = 0.08; // 8 cm frame profile
  const halfWinW = WIN_W / 2;
  const winTop = WIN_SILL + WIN_H;

  // ── Materials ─────────────────────────────────────────
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x4a5560, roughness: 0.7, metalness: 0.1,
  });

  const floorMat = new THREE.MeshStandardMaterial({
    color: 0x3a3530, roughness: 0.55, metalness: 0.05,
  });

  const emissiveMat = new THREE.MeshStandardMaterial({
    color: 0x4466aa, roughness: 0.5, metalness: 0.3,
    emissive: 0x334466, emissiveIntensity: 0.4,
  });

  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x3a3a40, roughness: 0.4, metalness: 0.3,
  });

  const canopyMat = new THREE.MeshStandardMaterial({
    color: 0x334455, roughness: 0.3, metalness: 0.4,
  });

  const darkMat = new THREE.MeshStandardMaterial({
    color: 0x2a2a30, roughness: 0.4, metalness: 0.2,
  });

  // ── Floor ─────────────────────────────────────────────
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, L), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = 0;
  cabin.add(floor);

  // ── Ceiling ───────────────────────────────────────────
  const ceiling = new THREE.Mesh(
    new THREE.PlaneGeometry(W, L),
    new THREE.MeshStandardMaterial({
      color: 0x3a4048, roughness: 0.65, metalness: 0.1,
      emissive: 0x1a2030, emissiveIntensity: 0.2,
    }),
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = H;
  cabin.add(ceiling);

  // ── Side walls with window apertures ──────────────────
  // Each side wall = 4 segments: below window, above window,
  // forward of window, aft of window. Plus a rounded frame ring.

  _buildWallWithWindow(cabin, 'port', -halfW, wallMat, frameMat,
    halfL, H, WIN_W, WIN_H, WIN_SILL, halfWinW, winTop, FRAME_WIDTH);

  _buildWallWithWindow(cabin, 'starboard', halfW, wallMat, frameMat,
    halfL, H, WIN_W, WIN_H, WIN_SILL, halfWinW, winTop, FRAME_WIDTH);

  // ── Rear bulkhead ─────────────────────────────────────
  const rearWall = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
  rearWall.position.set(0, H / 2, -halfL);
  cabin.add(rearWall);

  // ── Cockpit canopy ────────────────────────────────────
  const canopyH = H * 0.9;
  const glassMat = createGlassMaterial();
  const glassMeshes: THREE.Mesh[] = [];

  const frontPanel = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.7, canopyH), glassMat);
  frontPanel.position.set(0, canopyH / 2, halfL);
  frontPanel.rotation.x = -0.15;
  frontPanel.renderOrder = 1;
  frontPanel.name = 'canopy-front';
  cabin.add(frontPanel);
  glassMeshes.push(frontPanel);

  const leftCanopy = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.3, canopyH), glassMat);
  leftCanopy.position.set(-W * 0.35, canopyH / 2, halfL * 0.9);
  leftCanopy.rotation.y = Math.PI / 4;
  leftCanopy.rotation.x = -0.1;
  leftCanopy.renderOrder = 1;
  leftCanopy.name = 'canopy-left';
  cabin.add(leftCanopy);
  glassMeshes.push(leftCanopy);

  const rightCanopy = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.3, canopyH), glassMat);
  rightCanopy.position.set(W * 0.35, canopyH / 2, halfL * 0.9);
  rightCanopy.rotation.y = -Math.PI / 4;
  rightCanopy.rotation.x = -0.1;
  rightCanopy.renderOrder = 1;
  rightCanopy.name = 'canopy-right';
  cabin.add(rightCanopy);
  glassMeshes.push(rightCanopy);

  // ── Side window glass panes ────────────────────────────
  const sideGlassGeo = new THREE.PlaneGeometry(WIN_W - 0.02, WIN_H - 0.02);
  // Port
  const portGlass = new THREE.Mesh(sideGlassGeo, glassMat);
  portGlass.position.set(-halfW, WIN_SILL + WIN_H / 2, 0);
  portGlass.rotation.y = Math.PI / 2;
  portGlass.renderOrder = 1;
  portGlass.name = 'glass-port';
  cabin.add(portGlass);
  glassMeshes.push(portGlass);

  // Starboard
  const stbdGlass = new THREE.Mesh(sideGlassGeo, glassMat);
  stbdGlass.position.set(halfW, WIN_SILL + WIN_H / 2, 0);
  stbdGlass.rotation.y = -Math.PI / 2;
  stbdGlass.renderOrder = 1;
  stbdGlass.name = 'glass-starboard';
  cabin.add(stbdGlass);
  glassMeshes.push(stbdGlass);

  // ── Canopy mullions ───────────────────────────────────
  const mullionGeo = new THREE.BoxGeometry(0.04, canopyH * 0.95, 0.04);
  const mullionMat = frameMat;
  for (let sx = -1; sx <= 1; sx += 2) {
    const mullion = new THREE.Mesh(mullionGeo, mullionMat);
    mullion.position.set(sx * W * 0.17, canopyH / 2, halfL - 0.01);
    cabin.add(mullion);
  }

  // ── Ceiling light strips ──────────────────────────────
  const stripGeo = new THREE.PlaneGeometry(W - 0.3, 0.08);
  const lightStrip = new THREE.Mesh(stripGeo, emissiveMat);
  lightStrip.position.set(0, H - 0.05, 0);
  lightStrip.rotation.x = -Math.PI / 2;
  cabin.add(lightStrip);

  // ── Side consoles ─────────────────────────────────────
  const consoleGeo = new THREE.BoxGeometry(0.3, 0.06, L * 0.8);
  const leftConsole = new THREE.Mesh(consoleGeo, darkMat);
  leftConsole.position.set(-halfW + 0.2, 1.0, 0);
  cabin.add(leftConsole);

  const rightConsole = new THREE.Mesh(consoleGeo, darkMat);
  rightConsole.position.set(halfW - 0.2, 1.0, 0);
  cabin.add(rightConsole);

  // ── Yoke column (center, ~1.05m height) ───────────────
  const yokeColumn = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.08, 0.4, 16),
    new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.4, metalness: 0.6 }),
  );
  yokeColumn.position.set(0, 0.85, halfL - 0.6);
  yokeColumn.name = 'yoke-column';
  cabin.add(yokeColumn);

  // Yoke wheel (grabbable)
  const yokeWheel = new THREE.Mesh(
    new THREE.TorusGeometry(0.18, 0.035, 12, 24),
    new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.3, metalness: 0.8 }),
  );
  yokeWheel.position.set(0, 1.05, halfL - 0.6);
  yokeWheel.name = 'yoke-wheel';
  cabin.add(yokeWheel);

  // Yoke spokes
  for (let a = 0; a < Math.PI * 2; a += Math.PI) {
    const spoke = new THREE.Mesh(
      new THREE.CylinderGeometry(0.015, 0.015, 0.36, 8),
      new THREE.MeshStandardMaterial({ color: 0x777777, roughness: 0.3, metalness: 0.7 }),
    );
    spoke.position.set(Math.cos(a) * 0.09, 1.05, halfL - 0.6 + Math.sin(a) * 0.09);
    spoke.rotation.z = Math.PI / 2;
    spoke.rotation.y = a;
    spoke.name = 'yoke-spoke';
    cabin.add(spoke);
  }

  // ── Throttle lever (right console, grabbable) ─────────
  const throttleBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.08, 0.1),
    new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.3, metalness: 0.7 }),
  );
  throttleBase.position.set(halfW - 0.15, 0.94, halfL - 0.4);
  throttleBase.name = 'throttle-base';
  cabin.add(throttleBase);

  const throttleHandle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.025, 0.025, 0.15, 12),
    new THREE.MeshStandardMaterial({ color: 0xcc4444, roughness: 0.3, metalness: 0.6 }),
  );
  throttleHandle.position.set(halfW - 0.15, 1.0, halfL - 0.4);
  throttleHandle.name = 'throttle-handle';
  cabin.add(throttleHandle);

  return { cabin, glassMeshes };
}

/** Build one side wall with a framed window aperture */
function _buildWallWithWindow(
  parent: THREE.Group,
  _side: 'port' | 'starboard',
  xPos: number,
  wallMat: THREE.MeshStandardMaterial,
  frameMat: THREE.MeshStandardMaterial,
  halfL: number,
  H: number,
  winW: number,
  winH: number,
  winSill: number,
  halfWinW: number,
  winTop: number,
  frameW: number,
): void {
  const facing = xPos < 0 ? Math.PI / 2 : -Math.PI / 2; // rotate so PlaneGeometry faces into cabin

  // Helper: create wall segment PlaneGeometry
  function wallPanel(w: number, h: number, cx: number, cy: number, cz: number): THREE.Mesh {
    const geo = new THREE.PlaneGeometry(w, h);
    const mesh = new THREE.Mesh(geo, wallMat);
    mesh.position.set(xPos, cy, cz);
    mesh.rotation.y = facing;
    _placeAt(mesh, cx, cy);
    return mesh;
  }

  // 1. Below window: full wall width × sill height
  const below = wallPanel(winW + frameW * 2, winSill, 0, winSill / 2, 0);
  parent.add(below);

  // 2. Above window: full wall width × (H - winTop)
  const above = wallPanel(winW + frameW * 2, H - winTop, 0, winTop + (H - winTop) / 2, 0);
  parent.add(above);

  // 3. Forward of window
  const fwdW = halfL - halfWinW;
  if (fwdW > 0.01) {
    const fwd = wallPanel(fwdW, H, halfWinW + fwdW / 2, H / 2, 0);
    parent.add(fwd);
  }

  // 4. Aft of window
  const aftW = halfL - halfWinW;
  if (aftW > 0.01) {
    const aft = wallPanel(aftW, H, -(halfWinW + aftW / 2), H / 2, 0);
    parent.add(aft);
  }

  // ── Frame around window aperture ──────────────────────
  // Top frame bar: spans winW + 2×frameW, centered on window top
  const topBar = new THREE.Mesh(
    new THREE.BoxGeometry(frameW, frameW, winW + frameW * 2),
    frameMat,
  );
  topBar.position.set(xPos, winTop, 0);
  parent.add(topBar);

  // Bottom frame bar (sill)
  const botBar = new THREE.Mesh(
    new THREE.BoxGeometry(frameW, frameW, winW + frameW * 2),
    frameMat,
  );
  botBar.position.set(xPos, winSill, 0);
  parent.add(botBar);

  // Forward vertical frame bar
  const fwdVert = new THREE.Mesh(
    new THREE.BoxGeometry(frameW, winH, frameW),
    frameMat,
  );
  fwdVert.position.set(xPos, winSill + winH / 2, halfWinW + frameW / 2);
  parent.add(fwdVert);

  // Aft vertical frame bar
  const aftVert = new THREE.Mesh(
    new THREE.BoxGeometry(frameW, winH, frameW),
    frameMat,
  );
  aftVert.position.set(xPos, winSill + winH / 2, -(halfWinW + frameW / 2));
  parent.add(aftVert);

  // Rounded inner edge planes (one on each side of the frame)
  // These are thin strips that bridge wall→opening
  for (const sign of [-1, 1]) {
    // Top inner edge
    const topInner = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, frameW, winW),
      frameMat,
    );
    topInner.position.set(xPos + sign * (frameW / 2 - 0.01), winTop, 0);
    parent.add(topInner);

    // Bottom inner edge
    const botInner = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, frameW, winW),
      frameMat,
    );
    botInner.position.set(xPos + sign * (frameW / 2 - 0.01), winSill, 0);
    parent.add(botInner);

    // Forward vertical inner edge
    const fwdInner = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, winH, frameW),
      frameMat,
    );
    fwdInner.position.set(xPos + sign * (frameW / 2 - 0.01), winSill + winH / 2, halfWinW + frameW / 2);
    parent.add(fwdInner);

    // Aft vertical inner edge
    const aftInner = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, winH, frameW),
      frameMat,
    );
    aftInner.position.set(xPos + sign * (frameW / 2 - 0.01), winSill + winH / 2, -(halfWinW + frameW / 2));
    parent.add(aftInner);
  }
}

/** Place a PlaneGeometry mesh at a specific world (cx, cy) — unused since we handle position directly */
function _placeAt(_mesh: THREE.Mesh, _cx: number, _cy: number): void {
  // PlaneGeometry is already positioned correctly via position.set()
  // This is a no-op hook for potential future use
}
